import { normalizeSkillStatusPayload } from '../skills/skill-status';

export type SlashCommandSuggestion = {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly insertText: string;
  readonly kind: 'command' | 'skill';
};

export const BUILT_IN_SLASH_COMMANDS: readonly SlashCommandSuggestion[] = [
  {
    id: 'builtin-help',
    label: '/help',
    description: 'Show OpenClaw help',
    insertText: '/help',
    kind: 'command',
  },
  {
    id: 'builtin-commands',
    label: '/commands',
    description: 'Show available commands',
    insertText: '/commands',
    kind: 'command',
  },
  {
    id: 'builtin-skills',
    label: '/skills',
    description: 'List active skills',
    insertText: '/skills',
    kind: 'command',
  },
  {
    id: 'builtin-tools',
    label: '/tools',
    description: 'Show current tools',
    insertText: '/tools',
    kind: 'command',
  },
  {
    id: 'builtin-agents',
    label: '/agents',
    description: 'List thread-bound agents',
    insertText: '/agents',
    kind: 'command',
  },
  {
    id: 'builtin-model',
    label: '/model',
    description: 'Show or change the model',
    insertText: '/model',
    kind: 'command',
  },
  {
    id: 'builtin-clear',
    label: '/clear',
    description: 'Clear the current chat context',
    insertText: '/clear',
    kind: 'command',
  },
  {
    id: 'builtin-compact',
    label: '/compact',
    description: 'Compact session context',
    insertText: '/compact',
    kind: 'command',
  },
];

export function getSlashCommandQuery(input: string): string | null {
  if (!input.startsWith('/')) return null;
  if (input.includes('\n')) return null;
  const trimmedStart = input.trimStart();
  if (trimmedStart !== input) return null;
  const query = input.slice(1);
  if (query.includes(' ')) return null;
  return query.toLowerCase();
}

export function buildSlashCommandSuggestions(
  suggestions: readonly SlashCommandSuggestion[],
  query: string | null,
): readonly SlashCommandSuggestion[] {
  if (query == null) return [];
  return suggestions
    .filter((suggestion) =>
      suggestion.label.toLowerCase().slice(1).includes(query),
    )
    .slice(0, 10);
}

export function buildSkillCommandSuggestions(
  payload: unknown,
): readonly SlashCommandSuggestion[] {
  const skills = normalizeSkillStatusPayload(payload);
  return skills
    .filter((skill) => {
      if (typeof skill.name !== 'string' || skill.name.length === 0) {
        return false;
      }
      if (skill.enabled === false || skill.eligible === false) {
        return false;
      }
      return true;
    })
    .flatMap((skill) => {
      const name = skill.name as string;
      const description =
        typeof skill.description === 'string' && skill.description.length > 0
          ? skill.description
          : `Run ${name}`;
      return [
        {
          id: `skill-direct-${name}`,
          label: `/${toOpenClawSkillCommandName(name)}`,
          description,
          insertText: `/${toOpenClawSkillCommandName(name)} `,
          kind: 'skill' as const,
        },
        {
          id: `skill-${name}`,
          label: `/skill ${name}`,
          description,
          insertText: `/skill ${name} `,
          kind: 'skill' as const,
        },
      ];
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function toOpenClawSkillCommandName(skillName: string): string {
  return skillName
    .replace(/^paradex[-_]/iu, '')
    .replace(/[^a-z0-9]+/giu, '_')
    .replace(/^_+|_+$/gu, '')
    .toLowerCase();
}
